import React, { Component } from 'react';
import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

/**
 * FBX 映射表：class2 → 模型文件名（放在 public/model/ 目录）
 * 你可以随时替换这里的文件名
 */
const FBX_MAP = {
    '一号车间': '湖州车间1.fbx',
    '二号车间': '湖州车间2.fbx',
    '三号车间1层': '湖州车间3-1.fbx',
    '三号车间2层': '湖州车间3-2.fbx',
    '三号车间3层': '湖州车间3-3.fbx',
    '三号车间': '湖州车间3.fbx',
    '四号车间': '湖州车间4.fbx',
    // 杭州
    '工厂': '杭州.fbx',
};

/** 车间中文 → 英文副标题映射 */
const WORKSHOP_EN = {
    '一号车间': 'Workshop No.1',
    '二号车间': 'Workshop No.2',
    '三号车间': 'Workshop No.3',
    '四号车间': 'Workshop No.4',
    'F1': 'Floor 1',
    'F2': 'Floor 2',
    'F3': 'Floor 3',
};

class ThreeMap extends Component {
    // 标记球贴地高度（拾取平面和球体都用这个值）
    static MARKER_Y = 2;
    constructor(props) {
        super(props);
        this.state = {
            fullscreen: false,
            loading: true,
            error: null,
            activeWorkshop: null, // 当前选中的车间 tab: '一号车间'|'二号车间'|'三号车间'|'四号车间'
            activeFloor: null,    // 三号车间子 tab: 'F1'|'F2'|'F3'
            pickingMode: false,   // 坐标拾取模式
            pickedCoord: null,    // 拾取到的坐标 [x, y, z]
            isZoomed: false,     // 是否放大视图（用于杭州厂区隐藏切换按钮）
        };
        // refs
        this.miniRef = React.createRef();
        this.fullRef = React.createRef();

        // three.js 实例
        this.renderer = null;
        this.scene = null;
        this.camera = null;
        this.controls = null;
        this.animFrameId = null;
        this.currentModelFile = null;
        this.loadedModel = null;
        this.pickMarker = null;    // 拾取点临时标记
        this._pickPlane = null;   // 拾取用的射线平面
        this._heightRaycaster = new THREE.Raycaster(); // 射线检测模型表面高度
        // 全景标记球
        this.markerMeshes = [
          
        ];
        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();
        this._lastSceneName = null;
        this._zoomFrameCount = 0; // 帧计数（用于节流 isZoomed 状态更新）
    }

    componentDidMount() {
        if (this.shouldShow()) {
            this.initThree(false);
            this.loadModel(this.props.class2);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const wasShowing = this.shouldShow(prevProps);
        const nowShowing = this.shouldShow();

        // 从"不显示"切换到"显示"时，需要初始化 Three.js
        if (!wasShowing && nowShowing) {
            setTimeout(() => {
                this.initThree(false);
                this.loadModel(this.props.class2);
            }, 50);
            return;
        }

        // 已在显示状态下，class2 变化时重新加载模型
        if (nowShowing && prevProps.class2 !== this.props.class2) {
            this.loadModel(this.props.class2);
        }

        // 全屏内三号车间切换楼层 tab 时，重新加载对应模型
        const { activeWorkshop, activeFloor } = this.state;
        if (
            prevState.activeFloor !== activeFloor &&
            activeWorkshop === '三号车间' &&
            activeFloor &&
            this.state.fullscreen
        ) {
            const newClass2 = this.getCurrentClass2();
            if (newClass2) this.loadModel(newClass2);
        }

        // 标记球数据或当前场景变化时刷新标记球（不分全屏/小窗口）
        if (
            prevProps.markerScenes !== this.props.markerScenes ||
            prevProps.currentSceneName !== this.props.currentSceneName
        ) {
            this.refreshMarkers();
        }
    }

    // 判断当前 props 是否应该显示组件
    shouldShow(props) {
        const p = props || this.props;
        if (!p.class1) return false;
        const hidden = ['航拍', '室外', '行政楼', '实验室'];
        return !hidden.includes(p.class1);
    }

    componentWillUnmount() {
        this.disposeThree();
    }

    // ─── Three.js 初始化 ──────────────────────────────────────────
    initThree(isFullscreen) {
        const container = isFullscreen ? this.fullRef.current : this.miniRef.current;
        if (!container) return;

        this.disposeThree();

        const w = container.clientWidth;
        const h = container.clientHeight;

        // Scene
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0x1a2a3a);
        this.scene.fog = new THREE.Fog(0x1a2a3a, 80, 300);

        // Camera
        this.camera = new THREE.PerspectiveCamera(50, w / h, 0.1, 2000);
        if (isFullscreen) {
            this.camera.position.set(0, 80, 80);
            this.camera.up.set(0, 1, 0);
        } else {
            this.camera.position.set(0, 80, 0.01);
            this.camera.up.set(0, 0, -1);
        }
        this.camera.lookAt(0, 0, 0);

        // Renderer
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.setSize(w, h);
        this.renderer.shadowMap.enabled = true;
        container.appendChild(this.renderer.domElement);

        // Lights
        const ambient = new THREE.AmbientLight(0xffffff, 2);
        this.scene.add(ambient);
        const dirLight = new THREE.DirectionalLight(0xffffff, 1.2);
        dirLight.position.set(100, 200, 100);
        dirLight.castShadow = true;
        this.scene.add(dirLight);
        const fillLight = new THREE.DirectionalLight(0x8888ff, 0.4);
        fillLight.position.set(-100, 50, -100);
        this.scene.add(fillLight);

        // OrbitControls（全屏才允许交互）
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enabled = isFullscreen;
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.08;
        this.controls.minPolarAngle = isFullscreen ? 0 : Math.PI / 2;
        this.controls.maxPolarAngle = isFullscreen ? Math.PI / 2.1 : Math.PI / 2;
        this.controls.target.set(0, 0, 0);
        this.controls.update();

        // 拾取平面：高度和标记球一致（MARKER_Y），射线打到的 y 就是小球高度
        this._pickPlane = new THREE.Mesh(
            new THREE.PlaneGeometry(2000, 2000),
            new THREE.MeshBasicMaterial({ visible: false, side: THREE.DoubleSide })
        );
        this._pickPlane.rotation.x = -Math.PI / 2;
        this._pickPlane.position.y = ThreeMap.MARKER_Y;
        // this.scene.add(this._pickPlane);

        // 重新把已加载的模型加回场景
        if (this.loadedModel) {
            this.scene.add(this.loadedModel);
        }

        this.isFullscreen = isFullscreen;
        this.startRender();

        // 渲染全景标记球
        this.refreshMarkers();

        this._resizeHandler = () => this.handleResize(isFullscreen);
        window.addEventListener('resize', this._resizeHandler);
    }

    disposeThree() {
        if (this.animFrameId) {
            cancelAnimationFrame(this.animFrameId);
            this.animFrameId = null;
        }
        if (this._resizeHandler) {
            window.removeEventListener('resize', this._resizeHandler);
            this._resizeHandler = null;
        }
        if (this.renderer) {
            this.renderer.dispose();
            const dom = this.renderer.domElement;
            if (dom && dom.parentNode) dom.parentNode.removeChild(dom);
            this.renderer = null;
        }
        if (this.controls) {
            this.controls.dispose();
            this.controls = null;
        }
        this.scene = null;
        this.camera = null;
        // 清除标记球
        this.markerMeshes = [];
    }

    handleResize(isFullscreen) {
        const container = isFullscreen ? this.fullRef.current : this.miniRef.current;
        if (!container || !this.renderer || !this.camera) return;
        const w = container.clientWidth;
        const h = container.clientHeight;
        this.camera.aspect = w / h;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(w, h);
    }

    startRender() {
        const loop = () => {
            this.animFrameId = requestAnimationFrame(loop);
            if (this.controls) this.controls.update();
            if (this.renderer && this.scene && this.camera) {
                this.renderer.render(this.scene, this.camera);
            }
            // 检测当前场景变化 → 刷新标记球颜色
            if (this._lastSceneName !== this.props.currentSceneName) {
                this._lastSceneName = this.props.currentSceneName;
                console.log(this._lastSceneName)
                this.refreshMarkers();
            }
            // 节流：每 5 帧检测一次是否放大（杭州厂区据此隐藏切换按钮）
            this._zoomFrameCount++;
            if (this._zoomFrameCount % 5 === 0 && this.controls) {
                const dist = this.controls.getDistance();
                const zoomed = dist < 60; // 放大时距离变小，阈值可调
                if (zoomed !== this.state.isZoomed) {
                    this.setState({ isZoomed: zoomed });
                }
            }
        };
        loop();
    }

    // ─── 渲染全景标记球 ─────────────────────────────────────────
    refreshMarkers() {
        if (!this.scene) return;
        const { markerScenes = [], currentSceneName = '' } = this.props;

        // 清除旧标记球
        for (const m of this.markerMeshes) {
            this.scene.remove(m);
        }
        this.markerMeshes = [];

        if (!markerScenes.length) return;

        // 如果所有标记都在原点附近，说明坐标未设置，跳过渲染
        const allZero = markerScenes.every(m =>
            Math.abs(m.pos3d[0]) < 0.01 && Math.abs(m.pos3d[1]) < 0.01 && Math.abs(m.pos3d[2]) < 0.01
        );
        if (allZero) return;
        if(!this.loadedModel)return
        // ── 射线检测每个标记点的模型表面高度 ──
        // 从 (x, 100, z) 向下打射线，交点即为模型表面高度
        // const RAY_HEIGHT = 10000; // 射线起点高度（足够高）
        // let lastY = 0
        for (const scene of markerScenes) {
            const [x, y , z] = scene.pos3d;
            const isCurrent = scene.sceneName === currentSceneName;

            // 射线向下打，获取模型表面高度
            // if (this.loadedModel) {
            //     this._heightRaycaster.set(new THREE.Vector3(x, RAY_HEIGHT, z), new THREE.Vector3(0, -1, 0));
            //     const hits = this._heightRaycaster.intersectObject(this.loadedModel, true);
            //     if (hits.length > 0) {
            //         console.log(hits[0].point)
            //         lastY = hits[0].point.y;
            //     }
            // }else{
            // }
            // console.log(lastY)
            // console.log(x)
            // console.log(z)

            const geometry = new THREE.SphereGeometry(isCurrent ? 1.8 : 1.4, 16, 16);
            const material = new THREE.MeshBasicMaterial({
                color: isCurrent ? 0xff3333 : 0x3388ff,
                transparent: true,
                opacity: 0.85,
                depthWrite: false,
            });
            const mesh = new THREE.Mesh(geometry, material);
            mesh.position.set(x, parseFloat(y) + 2, z);
            // console.log(mesh.position)
            mesh.userData = {
                type: 'marker',
                sceneName: scene.sceneName,
                class2: scene.class2,
                isCurrent,
            };

            this.scene.add(mesh);
            this.markerMeshes.push(mesh);
        }
    }

    // ─── 射线检测点击标记球 ──────────────────────────────────────
    handleCanvasClick = (e) => {
        if (!this.state.fullscreen) return;
        const container = this.fullRef.current;
        if (!container) return;

        const rect = container.getBoundingClientRect();
        this.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

        this.raycaster.setFromCamera(this.mouse, this.camera);

        // 坐标拾取模式：射线命中地面/模型表面
        if (this.state.pickingMode) {
            const targets = [];
            if (this.loadedModel) targets.push(this.loadedModel);
            if (this._pickPlane) targets.push(this._pickPlane);
            if (!targets.length) return;

            const hits = this.raycaster.intersectObjects(targets, true);
            if (hits.length > 0) {
                const pt = hits[0].point;
                // Y 直接用射线命中位置（拾取平面在 MARKER_Y 高度，和小球一致）
                const coord = [parseFloat(pt.x.toFixed(1)), parseFloat(pt.y.toFixed(1)), parseFloat(pt.z.toFixed(1))];
                this.setState({ pickedCoord: coord });
                this._showPickMarker(pt);
            }
            return;
        }

        // 正常模式：点击蓝色标记球跳转
        const intersects = this.raycaster.intersectObjects(this.markerMeshes);
        for (const hit of intersects) {
            const marker = hit.object;
            if (marker.userData.type === 'marker' && !marker.userData.isCurrent) {
                const { sceneName } = marker.userData;
                if (sceneName && this.props.onMarkerClick) {
                    this.props.onMarkerClick(sceneName);
                     this.setState({ fullscreen: false, activeWorkshop: null, activeFloor: null }, () => {
                        this.initThree(false);
                    });
                }
                break;
            }
        }
    }

    // ─── 坐标拾取模式切换 ──────────────────────────────────────
    togglePickingMode = () => {
        this.setState(prev => ({
            pickingMode: !prev.pickingMode,
            pickedCoord: null,
        }), () => {
            const { pickingMode } = this.state;
            if (this.fullRef.current) {
                this.fullRef.current.style.cursor = pickingMode ? 'crosshair' : '';
            }
            // 清除拾取标记
            if (!pickingMode && this.pickMarker && this.scene) {
                this.scene.remove(this.pickMarker);
                this.pickMarker = null;
            }
        });
    }

    // ─── 拾取点标记（绿色十字球）─────────────────────────────────
    _showPickMarker(point) {
        if (this.pickMarker && this.scene) {
            this.scene.remove(this.pickMarker);
        }
        const geo = new THREE.SphereGeometry(0.8, 12, 12);
        const mat = new THREE.MeshBasicMaterial({ color: 0x44ff88, transparent: true, opacity: 0.9 });
        this.pickMarker = new THREE.Mesh(geo, mat);
        this.pickMarker.position.copy(point);
        if (this.scene) this.scene.add(this.pickMarker);
    }

    // ─── 复制坐标到剪贴板 ──────────────────────────────────────
    copyCoord = () => {
        const c = this.state.pickedCoord;
        if (!c) return;
        const text = `[${c[0]}, ${c[1]}, ${c[2]}]`;
        navigator.clipboard.writeText(text).catch(() => {});
    }

    // ─── 鼠标移动 → hover 变手型 ──────────────────────────────────
    handleCanvasMouseMove = (e) => {
        if (!this.state.fullscreen) return;
        const container = this.fullRef.current;
        if (!container) return;

        const rect = container.getBoundingClientRect();
        this.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

        this.raycaster.setFromCamera(this.mouse, this.camera);
        const intersects = this.raycaster.intersectObjects(this.markerMeshes);

        let isHovering = false;
        for (const hit of intersects) {
            if (hit.object.userData.type === 'marker' && !hit.object.userData.isCurrent) {
                isHovering = true;
                break;
            }
        }
        container.style.cursor = isHovering ? 'pointer' : '';
    }

    // ─── 加载 FBX 模型 ───────────────────────────────────────────
    loadModel(class2) {
        const filename = FBX_MAP[class2];
        if (!filename) {
            this.setState({ loading: false, error: `暂无"${class2}"对应模型` });
            return;
        }
        if (filename === this.currentModelFile) return;
        this.currentModelFile = filename;
        this.setState({ loading: true, error: null });

        if (this.loadedModel && this.scene) {
            this.scene.remove(this.loadedModel);
            this.loadedModel = null;
        }
        
        const loader = new FBXLoader();
        loader.load(
            `./model/${filename}`,
            (fbx) => {
                const box = new THREE.Box3().setFromObject(fbx);
                const center = box.getCenter(new THREE.Vector3());
                const size = box.getSize(new THREE.Vector3());
                const maxDim = Math.max(size.x, size.y, size.z);
                const scale = 60 / maxDim;
                fbx.scale.setScalar(scale);
                fbx.position.sub(center.multiplyScalar(scale));

                fbx.traverse(child => {
                    if (child.isMesh) {
                        // child.castShadow = true;
                        // child.receiveShadow = true;
                        if (child.material) {
                            const mats = Array.isArray(child.material) ? child.material : [child.material];
                            mats.forEach(m => {
                                m.metalness = 0.1;
                                m.roughness = 0.7;
                            });
                        }
                    }
                });

                this.loadedModel = fbx;
                if (this.scene) this.scene.add(fbx);
                this.renderer.render(this.scene, this.camera);
                this.setState({ loading: false });
                setTimeout(() => {
                this.refreshMarkers();
                    
                }, 200);
            },
            undefined,
            (err) => {
                console.error('FBX加载失败', err);
                this.setState({ loading: false, error: '模型加载失败' });
            }
        );
    }

    // ─── 全屏切换 ─────────────────────────────────────────────────
    openFullscreen = () => {
        const { class2 } = this.props;
        // 根据当前 class2 推算 activeWorkshop 和 activeFloor
        const workshop = this.getWorkshopOfClass2(class2);
        const floor = this.getFloorOfClass2(class2);
        this.setState({ fullscreen: true, activeWorkshop: workshop, activeFloor: floor }, () => {
            this.initThree(true);
        });
    }

    closeFullscreen = () => {
        this.setState({ fullscreen: false, activeWorkshop: null, activeFloor: null }, () => {
            this.initThree(false);
        });
    }


    // ─── 根据 class2 判断属于哪个车间 ─────────────────────────────
    getWorkshopOfClass2(class2) {
        if (!class2) return null;
        if (class2.includes('一号')) return '一号车间';
        if (class2.includes('二号')) return '二号车间';
        if (class2.includes('三号')) return '三号车间';
        if (class2.includes('四号')) return '四号车间';
        return null;
    }

    // ─── 根据 class2 判断楼层（F1/F2/F3）──────────────────────────
    getFloorOfClass2(class2) {
        if (!class2) return null;
        if (class2.includes('1层') || class2.includes('1f')) return 'F1';
        if (class2.includes('2层') || class2.includes('2f')) return 'F2';
        if (class2.includes('3层') || class2.includes('3f')) return 'F3';
        return null;
    }

    // ─── 获取车间 tab 列表（按 modelList 中有模型的） ─────────────
    getWorkshopTabs() {
        const { modelList } = this.props;
        if (!modelList || !modelList.length) return [];
        const workshopMap = {};
        for (const item of modelList) {
            if (!FBX_MAP[item.class2]) continue;
            const ws = this.getWorkshopOfClass2(item.class2);
            if (ws) workshopMap[ws] = true;
        }
        return Object.keys(workshopMap);
    }

    // ─── 三号车间的楼层列表 ────────────────────────────────────────
    getFloorTabs() {
        const { modelList } = this.props;
        if (!modelList) return [];
        const floors = [];
        for (const item of modelList) {
            if (!FBX_MAP[item.class2]) continue;
            if (!item.class2.includes('三号')) continue;
            const f = this.getFloorOfClass2(item.class2);
            if (f && !floors.includes(f)) floors.push(f);
        }
        return floors.sort();
    }

    // ─── 切换车间 tab ─────────────────────────────────────────────
    handleWorkshopChange(ws) {
        if (ws === '三号车间') {
            const floors = this.getFloorTabs();
            const firstFloor = floors[0] || null;
            this.setState({ activeWorkshop: ws, activeFloor: firstFloor }, () => {
                // 通知父组件更新 sceneClass2Sub → 刷新标记球
                const c2 = this.getCurrentClass2();
                if (c2 && this.props.onModelChange) this.props.onModelChange(this.props.class1, c2);
            });
        } else {
            this.loadModel(ws);
            this.setState({ activeWorkshop: ws, activeFloor: null }, () => {
                if (ws && this.props.onModelChange) this.props.onModelChange(this.props.class1, ws);
            });
        }
    }

    // ─── 切换楼层 tab ─────────────────────────────────────────────
    handleFloorChange(floor) {
        this.setState({ activeFloor: floor }, () => {
            const c2 = this.getCurrentClass2();
            if (c2) {
                this.loadModel(c2);
                if (this.props.onModelChange) this.props.onModelChange(this.props.class1, c2);
            }
        });
    }

    // ─── 当前选中车间对应的 class2 ────────────────────────────────
    getCurrentClass2() {
        const { activeWorkshop, activeFloor } = this.state;
        const { class2 } = this.props;
        if (activeWorkshop === '三号车间' && activeFloor) {
            // 从楼层找对应 class2
            const floorMap = { 'F1': '1层', 'F2': '2层', 'F3': '3层' };
            const suffix = floorMap[activeFloor];
            const { modelList } = this.props;
            const found = modelList.find(item =>
                item.class2 && item.class2.includes('三号') && item.class2.includes(suffix)
            );
            return found ? found.class2 : null;
        }
        return class2;
    }

    // ─── 当前车间/楼层下的所有模型项 ─────────────────────────────
    getCurrentModels() {
        const { activeWorkshop, activeFloor } = this.state;
        const { modelList } = this.props;
        if (!modelList || !activeWorkshop) return [];

        return modelList.filter(item => {
            const ws = this.getWorkshopOfClass2(item.class2);
            if (ws !== activeWorkshop) return false;
            // 三号车间按楼层过滤
            if (activeWorkshop === '三号车间') {
                const f = this.getFloorOfClass2(item.class2);
                return f === activeFloor;
            }
            return true;
        });
    }

    // ─── 渲染 ─────────────────────────────────────────────────────
    render() {
        const { class2 } = this.props;
        const { fullscreen, loading, error, activeWorkshop, activeFloor } = this.state;

        if (!this.shouldShow()) {
            return null;
        }

        const workshopTabs = this.getWorkshopTabs();
        const floorTabs = this.getFloorTabs();
        const currentWs = workshopTabs.includes(activeWorkshop) ? activeWorkshop : workshopTabs[0];
        const currentFloor = floorTabs.includes(activeFloor) ? activeFloor : floorTabs[0];

        // 当前选中的 class2
        const currentClass2 = this.getCurrentClass2();
        const currentModels = this.getCurrentModels();

        return (
            <>
                {/* ── 左下角小窗口 ── */}
                <div className="threemap-mini" onClick={this.openFullscreen}>
                    <div className="threemap-mini-canvas" ref={this.miniRef}>
                        {loading && <div className="threemap-loading">加载中…</div>}
                        {error && <div className="threemap-error">{error}</div>}
                    </div>
                    <div className="threemap-mini-label">{currentClass2 || class2}</div>
                    <div className="threemap-mini-hint">点击查看全景沙盘</div>
                </div>

                {/* ── 全屏弹窗 ── */}
                {fullscreen && (
                    <div className="threemap-fullscreen">
                        {/* 模型视图区 */}
                        <div className="threemap-full-canvas" ref={this.fullRef} onClick={this.handleCanvasClick} onMouseMove={this.handleCanvasMouseMove}>
                            {loading && <div className="threemap-loading">加载中…</div>}
                            {error && <div className="threemap-error">{error}</div>}
                        </div>

                        {/* ── 顶部 Tab 区（杭州厂区放大后隐藏） ── */}
                        {/* 杭州厂区 class2='工厂'，放大时 isZoomed=true，两个都满足则隐藏 */}
                        {!(class2 === '工厂') && (
                        <div className="threemap-model-panel">
                            {/* 车间大 Tab */}
                            {workshopTabs.length > 0 && (
                                <div className="threemap-tabs">
                                    {workshopTabs.map(ws => (
                                        <div
                                            key={ws}
                                            className={`threemap-tab ${ws === currentWs ? 'active' : ''}`}
                                            onClick={() => this.handleWorkshopChange(ws)}
                                        >
                                            <span className="threemap-tab-cn">{ws}</span>
                                            <span className="threemap-tab-en">{WORKSHOP_EN[ws] || ws}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* 三号车间子 Tab（F1/F2/F3） */}
                            {currentWs === '三号车间' && floorTabs.length > 0 && (
                                <div className="threemap-floor-tabs">
                                    {floorTabs.map(f => (
                                        <div
                                            key={f}
                                            className={`threemap-floor-tab ${f === currentFloor ? 'active' : ''}`}
                                            onClick={() => this.handleFloorChange(f)}
                                        >
                                            <span className="threemap-tab-cn">{f}</span>
                                            <span className="threemap-tab-en">{WORKSHOP_EN[f] || f}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* 白色内容区 — 列出当前车间下所有模型 */}
                            {/* <div className="threemap-tab-content">
                                {currentModels.map(item => {
                                    const isActive = item.class2 === currentClass2;
                                    return (
                                        <div
                                            key={item.class2}
                                            className={`threemap-model-item ${isActive ? 'active' : ''}`}
                                            onClick={() => {
                                                if (!isActive) this.switchModel(item.class2);
                                            }}
                                        >
                                            {item.class2}
                                        </div>
                                    );
                                })}
                            </div> */}
                        </div>
                        )}

                        {/* 关闭按钮 */}
                        <button className="threemap-close-btn" onClick={this.closeFullscreen}>✕ 关闭</button>

                        {/* ── 坐标拾取按钮 ── */}
                         {/* <button
                            className={`threemap-pick-btn ${this.state.pickingMode ? 'active' : ''}`}
                            onClick={this.togglePickingMode}
                            title="在3D场景中拾取坐标点"
                        >
                            {this.state.pickingMode ? '☑ 拾取中…' : '⊕ 拾取坐标'}
                        </button>  */}

                        {/* ── 拾取结果弹窗 ── */}
                        {this.state.pickedCoord && (
                            <div className="threemap-pick-toast">
                                <div className="threemap-pick-label">拾取到的坐标：</div>
                                <div className="threemap-pick-coord">
                                    [{this.state.pickedCoord[0]}, {this.state.pickedCoord[1]}, {this.state.pickedCoord[2]}]
                                </div>
                                <div className="threemap-pick-hint">复制后粘贴到场景数据的 pos3d 字段</div>
                                <button className="threemap-pick-copy" onClick={this.copyCoord}>复制</button>
                                <button className="threemap-pick-close" onClick={() => this.setState({ pickedCoord: null })}>✕</button>
                            </div>
                        )}
                        <div className="threemap-full-tip">鼠标拖动旋转 · 滚轮缩放</div>
                    </div>
                )}
            </>
        );
    }
}

export default ThreeMap;
